import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  Grid,
  Typography,
  Button,  
  darken,
} from "@mui/material";
import {
  PlaylistAddCheck as PlaylistAddCheckIcon,
  PlaylistAdd as PlaylistAddIcon,
  SyncProblem as SyncProblemIcon,
  PlaylistRemove as PlaylistRemoveIcon,
} from "@mui/icons-material";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { useAppSelector } from "../../../../../../../application/states/hooks";
import { theme } from "../../../../../../styles";

interface DialogEconomicActivityListPreviewProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

// Componente para renderizar cada linha com efeito striped
const RenderRow = ({ data, index, style }: ListChildComponentProps) => {
  const item = data[index];
  return (
    <div
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        backgroundColor: index % 2 ? "#f5f5f5" : "#ffffff",
        paddingLeft: "16px",
        borderBottom: "1px solid #e0e0e0",
        fontFamily: "monospace",
        fontSize: "0.875rem",
      }}
    >
      {item}
    </div>
  );
};

export const DialogEconomicActivityListPreview = ({
  open,
  onClose,
  onConfirm,
}: DialogEconomicActivityListPreviewProps) => {
  const economicActivityList = useAppSelector(
    (state) => state.economicActivityList,
  );

  const { company } = useAppSelector((state) => state.company);

  // Determinar o modo atual baseado no ID de edição
  const isEditMode = Boolean(
    economicActivityList.editEconomicActivityListForm?.fields?.id &&
    economicActivityList.editEconomicActivityListForm.fields.id !== "",
  );

  // Obter a lista atual baseada no modo
  const currentList = isEditMode
    ? economicActivityList.editEconomicActivityListForm?.fields
        ?.economicActivities || []
    : economicActivityList.createEconomicActivityListForm?.fields
        ?.economicActivities || [];

  const validValues = economicActivityList.fileAnalysis?.validValues || [];
  const repeatedList = economicActivityList.fileAnalysis?.repeatedList || [];

  // Valores inválidos são os que estão em filteredList mas não em validValues
  const invalidValues =
    economicActivityList.fileAnalysis?.filteredList?.filter(
      (value) => !validValues.includes(value),
    ) || [];

  // Contadores
  const currentListCount = currentList.length;
  const newActivitiesCount = validValues.length;
  const repeatedActivitiesCount = repeatedList.length;
  const invalidActivitiesCount = invalidValues.length;

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {          
          height: "600px",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <Typography
              fontWeight="bold"
              textTransform="uppercase"
              variant="h5"
              color="primary"
            >
              Pré-visualização da importação
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Arquivo: <strong>{economicActivityList.fileData?.name}</strong> |
              Coluna selecionada:{" "}
              <strong>
                {economicActivityList.selectedColumn || "Nenhuma"}
              </strong>{" "}
              | Modo: <strong>{isEditMode ? "EDIÇÃO" : "CRIAÇÃO"}</strong>
              {isEditMode && (
                <>
                  {" "}
                  | Lista:{" "}
                  <strong>
                    {
                      economicActivityList.editEconomicActivityListForm?.fields
                        ?.name
                    }
                  </strong>
                </>
              )}
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>

      <Box px={2}>
        <Divider />
      </Box>

      <DialogContent>
        <Grid
          container
          spacing={2}
          sx={{
            height: "100%",
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "row",
          }}
        >
          {/* CNAEs atuais */}
          <Grid item xs={12} md={3} sx={{ height: "100%" }}>
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <Typography
                color="primary"
                variant="subtitle1"
                align="center"
                fontWeight="bold"
                gutterBottom
              >
                <PlaylistAddCheckIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                CNAEs atuais ({currentListCount})
                {isEditMode && " (da lista em edição)"}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <AutoSizer>
                  {({ height, width }: { height: number; width: number }) => (
                    <FixedSizeList
                      height={height}
                      width={width}
                      itemSize={35}
                      itemCount={currentListCount}
                      itemData={currentList}
                      overscanCount={5}
                    >
                      {RenderRow}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </Box>
            </Box>
          </Grid>

          {/* CNAEs novos */}
          <Grid item xs={12} md={3} sx={{ height: "100%" }}>
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <Typography
                color="success.main"
                variant="subtitle1"
                align="center"
                fontWeight="bold"
                gutterBottom
              >
                <PlaylistAddIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                CNAEs novos ({newActivitiesCount})
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <AutoSizer>
                  {({ height, width }: { height: number; width: number }) => (
                    <FixedSizeList
                      height={height}
                      width={width}
                      itemSize={35}
                      itemCount={newActivitiesCount}
                      itemData={validValues}
                      overscanCount={5}
                    >
                      {RenderRow}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </Box>
            </Box>
          </Grid>

          {/* CNAEs repetidos */}
          <Grid item xs={12} md={3} sx={{ height: "100%" }}>
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <Typography
                color="warning.main"
                variant="subtitle1"
                align="center"
                fontWeight="bold"
                gutterBottom
              >
                <SyncProblemIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                CNAEs repetidos ({repeatedActivitiesCount})
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <AutoSizer>
                  {({ height, width }: { height: number; width: number }) => (
                    <FixedSizeList
                      height={height}
                      width={width}
                      itemSize={35}
                      itemCount={repeatedActivitiesCount}
                      itemData={repeatedList}
                      overscanCount={5}
                    >
                      {RenderRow}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </Box>
            </Box>
          </Grid>

          {/* CNAEs inválidos */}
          <Grid item xs={12} md={3} sx={{ height: "100%" }}>
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <Typography
                color="error.main"
                variant="subtitle1"
                align="center"
                fontWeight="bold"
                gutterBottom
              >
                <PlaylistRemoveIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                CNAEs inválidos ({invalidActivitiesCount})
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <AutoSizer>
                  {({ height, width }: { height: number; width: number }) => (
                    <FixedSizeList
                      height={height}
                      width={width}
                      itemSize={35}
                      itemCount={invalidActivitiesCount}
                      itemData={invalidValues}
                      overscanCount={5}
                    >
                      {RenderRow}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <Box px={2}>
        <Divider />
      </Box>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={newActivitiesCount === 0}
          sx={{
            "&:hover": {
              color:
                company?.resale === true || company?.resaleId
                  ? theme.palette.primary.contrastText
                  : theme.palette.primary.contrastText,
              backgroundColor:
                company?.resale === true || company?.resaleId
                  ? darken(theme.palette.primary.main, 0.2)
                  : darken(theme.palette.primary.main, 0.2),
              cursor: "default",
            },
          }}
        >
          Importar{" "}
          {newActivitiesCount > 0
            ? `${newActivitiesCount} CNAE${newActivitiesCount > 1 ? "s" : ""}`
            : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};